import { useRouteLoaderData } from '@remix-run/react';
import type {
  NavbarDocument,
  NavbarSerializableNode,
  TNavbarDropdownElement,
  TNavbarItemElement,
} from '@shared/dream-components';
import {
  applyThemeToNavbarNode,
  constructMobileView,
  getNavbarUserButtonContent,
  navbarV2Migrator,
} from '@shared/dream-components';
import { useEffect, useState } from 'react';

import type { Site } from '~/interfaces/site';

import isUserAgentMobile from '~/utils/isUserAgentMobile';

import type { RootLoaderData } from '~/root';

import NavbarRendererV2 from './NavbarRenderer';

interface NavbarSerializerProps {
  site: Site;
}

interface NavbarSerializerRenderProps {
  data: NavbarSerializableNode;
  isMobile: boolean;
}

declare global {
  interface Window {
    opera: any;
  }

  interface Navigator {
    userAgentData: {
      mobile: boolean;
    };
  }
}

const isMobileDevice = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  if (isUserAgentMobile(navigator.userAgent || navigator.vendor || window.opera)) {
    return true;
  }

  if (typeof navigator !== 'undefined' && navigator?.userAgentData?.mobile) {
    return true;
  }

  return (window?.innerWidth || 1024) < 768;
};

// Custom hook for detecting mobile viewport
const useIsMobile = (defaultValue: boolean) => {
  const [isMobile, setIsMobile] = useState<boolean>(defaultValue);

  useEffect(() => {
    setIsMobile(isMobileDevice());

    window?.addEventListener('resize', () => {
      setIsMobile(isMobileDevice());
    });
    return () =>
      window?.removeEventListener('resize', () => {
        setIsMobile(isMobileDevice());
      });
  }, []);

  return isMobile;
};

const serializeNode = (node: NavbarSerializableNode | NavbarSerializableNode[], screen: 'desktop' | 'mobile') => {
  if (!node) {
    return null;
  }

  if (Array.isArray(node)) {
    return node.map((node: NavbarSerializableNode, index: number) => (
      <NavbarRendererV2 key={`${index}-${node?.attrs?.id}`} node={node} screen={screen}>
        {'content' in node && node.content ? serializeNode(node.content as NavbarSerializableNode[], screen) : null}
      </NavbarRendererV2>
    ));
  }

  return (
    <NavbarRendererV2 node={node} screen={screen}>
      {'content' in node && node.content ? serializeNode(node.content as NavbarSerializableNode[], screen) : null}
    </NavbarRendererV2>
  );
};
const NavbarSerializerRender = ({ data, isMobile }: NavbarSerializerRenderProps) => {
  return serializeNode(data as NavbarSerializableNode | NavbarSerializableNode[], isMobile ? 'mobile' : 'desktop');
};

const applyUserButtonDropdown = (node: NavbarSerializableNode, site: Site) => {
  if (Array.isArray(node)) {
    node.forEach((childNode) => applyUserButtonDropdown(childNode, site));
    return;
  }

  // Handle User Button Dropdown Defaults
  if (
    node.attrs &&
    'type' in node.attrs &&
    node.attrs?.type === 'user_button' &&
    node.attrs?.action === 'profile' &&
    node.attrs?.style !== 'button'
  ) {
    // Only convert to dropdown if we have a theme and primary theme
    if (site?.theme_rules && site?.primary_theme) {
      node.attrs.style = 'dropdown';
      node.attrs.href = undefined;
      node.attrs.showWhen = 'all_logged_in';
      if (!('content' in node) || !node.content || node.content.length === 0) {
        let newContent = getNavbarUserButtonContent();
        newContent = applyThemeToNavbarNode(
          newContent,
          site?.primary_theme,
          site?.theme_rules,
        ) as TNavbarDropdownElement;
        (node as TNavbarItemElement).content = [newContent];
      }
    } else if (node.attrs?.style === undefined) {
      // If we don't have a theme and the style is undefined, set it to button
      node.attrs.style = 'button';
      node.attrs.href = undefined;
      node.attrs.showWhen = 'all_logged_in';
    }
  }

  // Recursively check children
  if ('content' in node && node.content && Array.isArray(node.content)) {
    node.content.forEach((childNode) => applyUserButtonDropdown(childNode, site));
  }
};

const NavbarSerializerV2 = ({ site }: NavbarSerializerProps) => {
  const { isUAStringMobile } = useRouteLoaderData('root') as RootLoaderData;
  const navbarData = site.viewable_site_version.navbar;

  const isMobile = useIsMobile(isUAStringMobile);

  if (!navbarData || isMobile === undefined) {
    return null;
  }

  if (navbarData.type === 'doc') {
    const typedNavbar = navbarData as NavbarDocument;
    const data = isMobile ? typedNavbar?.attrs?.data?.mobile : typedNavbar?.attrs?.data?.desktop;

    if (!data) {
      // Something went wrong with migration via the editor
      return null;
    }

    applyUserButtonDropdown(data, site);

    return <NavbarSerializerRender isMobile={isMobile} data={data} />;
  }

  // Navbar v1 JSON, migrate and construct device specific view
  const migratedData = navbarV2Migrator(navbarData);

  const desktopView = migratedData;
  const mobileView = constructMobileView(migratedData);
  const data = isMobile ? mobileView : desktopView;

  applyUserButtonDropdown(data, site);

  return <NavbarSerializerRender isMobile={isMobile} data={data} />;
};

export default NavbarSerializerV2;
